/**
 * @file   src\Pages\AddCarWash.tsx
 * @brief  AddCarWash page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { useState, useEffect, useOutletContext, Container, Badge, Button, Breadcrumb, Row, Col, useNavigate, useRef } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import CWSelect from '../components/CWSelect';
import CWInput from '../components/CWInput';
import CwoAddWashSuccessModal from '../components/CwoAddWashSuccessModal';
import { pageURLs } from '../utils/pageUrls';
import { AppContextType } from '../interfaces/generalInterface';
import { formatPhoneNumber, formatPhoneNumberforApi } from '../utils/helper';
import * as Yup from 'yup'
import { cwoaddCarwashSchema } from '../validations/cwoaddCarwashSchema';
import { CITY_MAX_LENGTH, EMAIL_MAX_LENGTH, NAME_MAX_LENGTH, PHONE_MAX_LENGTH, WASH_ADDRESS_MAX_LENGTH, WASH_NAME_MAX_LENGTH } from '../utils/constants';
import { MessageToaster } from '../utils/toastUtil';
import axios from '../config/axiosConfig'
import { CarWashApi } from '../utils/apiUrls';
import { CountryItem, Option } from '../interfaces/CarWashInterface';
import { DEFAULT_COUNTRY_CODE, fetchStatesByCountry, fetchSupportedCountries, getCountryFieldLabels, getPostalCodeMaxLength, isAllowedPostalCodeInput, mapCountryOptions, normalizePostalCodeInput } from '../utils/countryState';
import FullLoader from '../components/FullLoader';
import { useAppDispatch, useAppSelector } from '../hooks';
import { validateUser } from '../store/actions/authAction';
import CWPasswordInput from '../components/CWPasswordInput';
import Cookies from "js-cookie";
import { RootState } from '../store';
const Cwosignup = (props: any) => {


    // Declare action dispatch.
    const dispatch = useAppDispatch();
    const navigate = useNavigate()
    const { isSuccess, userInfo,isLoading, errorCode, errorMessage} = useAppSelector((state: RootState) => state.adminAuth);
    // Access the context
    const { data, updateData } = useOutletContext<AppContextType>();

    // State to handle options for country and state
    const [supportedCountries, setSupportedCountries] = useState<CountryItem[]>([]);
    const [options, setOptions] = useState<Option[]>([]);

    // Use a ref to store input elements
    const formRef = useRef<HTMLFormElement>(null);

    // Initialize states for formdata
    const [formData, setFormData] = useState({
        washname: '',
        address: '',
        address_2: '',
        city: '',
        country: DEFAULT_COUNTRY_CODE,
        state: '',
        zipcode: '',
        name: '',
        emailid: '',
        phonenumber: '',
        password: '',
        accountname: '',
        accountnumber: '',
        retypeaccountnumber: '',
        routingnumber: '',
    })
    const countryOptions = mapCountryOptions(supportedCountries);
    const { stateLabel, postalLabel } = getCountryFieldLabels(supportedCountries, formData.country);
    const postalMaxLength = getPostalCodeMaxLength(formData.country);

    // Initialise states for errors
    const [errors, setErrors] = useState({
        washname: '',
        address: '',
        address_2: '',
        city: '',
        country: '',
        state: '',
        zipcode: '',
        name: '',
        emailid: '',
        phonenumber: '',
        password: '',
        accountname: '',
        accountnumber: '',
        routingnumber: '',
        retypeaccountnumber: ''
    });

    const [loading, setLoading] = useState(false);

    // Instantiate MessageToaster
    const toaster = new MessageToaster();

    // To display heading
    useEffect(() => {
        handleUpdate()

        getCountries()
        getStates(DEFAULT_COUNTRY_CODE)
    }, [])


    //  login success change function.
    useEffect(() => {
        if (isSuccess && userInfo?.accessToken) {
            // console.log(userInfo?.accessToken)
            Cookies.set("accessToken", userInfo.accessToken, { expires: 365 * 10 });
            Cookies.set("refreshToken", userInfo.refreshToken, { expires: 365 * 10 });
            const { accessToken, refreshToken, ...userInfoWithoutTokens } = userInfo;

            localStorage.setItem("CARWASH_OWNER_DATA",JSON.stringify(userInfoWithoutTokens));
            localStorage.removeItem("CARWASH_ADMIN_DATA");//Remove carwash super admin session
        }
    }, [isSuccess]);

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "Sign Up", url: `/${pageURLs.manage_carwash}`, showBackButton: false, showMainMenu: false, activeLink: "manage-car-wash" });
    };

    const getCountries = async () => {
        try {
            const countries = await fetchSupportedCountries();
            setSupportedCountries(countries)
        } catch (error: any) {
            toaster.toastError(error.message);
        }
    }

    const getStates = async (countryCode: string) => {
        try {
            const stateOptions = await fetchStatesByCountry(countryCode);
            setOptions(stateOptions)
        } catch (error: any) {
            setOptions([])
            toaster.toastError(error.message);
        }
    }

    const [addedcarwashid, setAddedcarwashid] = useState(0);
    const [showAddSuccessModal, setShowAddSuccessModal] = useState(false);
    const handleCloseAddSuccessModal = () => setShowAddSuccessModal(false);
    const handleShowAddSuccessModal = () => setShowAddSuccessModal(true);

    // Navigate accordung to url
    const itemClick = async (url: string) => {
        navigate(url);
    }

    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption ? selectedOption.value : '';

        // To remove validation when user enters input
        var keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        if (name === 'country') {
            setErrors({
                ...errors,
                country: '',
                state: '',
                zipcode: '',
            })
            setFormData({
                ...formData,
                country: value,
                state: '',
                zipcode: '',
            });
            getStates(value);
            return;
        }

        setFormData({
            ...formData,
            [name]: value,
        });

    };


    // Triggers when input is entered
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {

        // To allow only digits in routingnumber field
        if (event.target.name === "routingnumber") {
            // Extract only digits from the input value
            const routingnumber_value = event.target.value;
            if (!/^\d*$/.test(routingnumber_value)) {
                event.target.value = routingnumber_value.replace(/\D/g, '');
            }
        }

        // To allow only digits in accountnumber field
        if (event.target.name === "accountnumber") {
            // Extract only digits from the input value
            const accountnumber_value = event.target.value;
            if (!/^\d*$/.test(accountnumber_value)) {
                event.target.value = accountnumber_value.replace(/\D/g, '');
            }
        }

        // To allow only digits in retypeaccountnumber field
        if (event.target.name === "retypeaccountnumber") {
            // Extract only digits from the input value
            const retypeaccountnumber_value = event.target.value;
            if (!/^\d*$/.test(retypeaccountnumber_value)) {
                event.target.value = retypeaccountnumber_value.replace(/\D/g, '');
            }
        }

        const { name, value } = event.target
        // To allow only digits in phone number field
        if ((event.target.name === "phonenumber")) {
            // Extract only digits from the input value
            const phone_value = event.target.value;
            if (!/^\d*$/.test(phone_value)) {
                event.target.value = phone_value.replace(/\D/g, '');
            }

            const { name, value } = event.target
            const formattedValue = formatPhoneNumber(value);

            setFormData({
                ...formData,
                [name]: formattedValue,
            });
        } else if ((event.target.name === "zipcode")) {

            const new_value = normalizePostalCodeInput(event.target.value, formData.country);
            if (isAllowedPostalCodeInput(new_value, formData.country)) {
                setFormData({
                    ...formData,
                    [name]: new_value,
                });
            }
        } else {
            setFormData({
                ...formData,
                [name]: value,
            });

        }


        // To remove validation when user enters input
        var keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }
    }

    // Handle the paste event to allow only 10 digits in phone number
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('Text');
        // Extract only numbers and limit to 10 characters
        const formattedData = pastedData.replace(/\D/g, '').slice(0, 10);
        const formattedValue = formatPhoneNumber(formattedData);
        setFormData({
            ...formData,
            phonenumber: formattedValue,
        });
    };



    // Triggers on submit
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        try {
            setLoading(true);
            // Validate form data
            await cwoaddCarwashSchema.validate(formData, { abortEarly: false });


            const newformData = {
                companyName: formData.washname.trim(),
                address1: formData.address.trim(),
                address2: formData.address_2.trim(),
                contactName: formData.name.trim(),
                email: formData.emailid.trim(),
                phoneNumber: formData.phonenumber.trim(),
                city: formData.city.trim(),
                countryCode: formData.country.trim(),
                state: formData.state.trim(),
                zipCode: formData.zipcode.trim(),
                password: formData.password.trim()
            };

            // Modify formData to format phone number
            newformData.phoneNumber = formatPhoneNumberforApi(newformData.phoneNumber);
            // API call to sign up
            try {
                const { data } = await axios.post(CarWashApi.CARWASH_OWNER_SIGNUP, newformData);

                if (data?.error === 0) {
                    let LoginFormData =
                    {
                        email: formData.emailid.trim(),
                        password: formData.password.trim(),
                        adminUserType: 'CARWASHOWNER'
                    }

                    dispatch(validateUser(LoginFormData));
                    setAddedcarwashid(data.data.carWashId);
                    handleShowAddSuccessModal();

                }
                else {
                    // show error message
                    toaster.toastError(data?.message);
                }
            } catch (error: any) {
                throw new Error(error?.response?.data?.message || error?.message);
            }
            finally {
                setLoading(false); // Hide loader
            }

        } catch (error) {

            const newErrors: any = {};

            //If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {

                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }

            // Focus on the first invalid field
            if (formRef.current) {
                const firstInvalidField = Object.keys(newErrors)[0];
                const invalidElement = formRef.current.querySelector<HTMLInputElement>(`[name="${firstInvalidField}"]`);
                if (invalidElement) {
                    invalidElement.focus();
                }
            }

        } finally {
            setLoading(false)
        }
    }

    return (
        <div className='manage-subscription-container'>
            <Container>

                <div className='cw-form-outer-wrap'>
                    <form onSubmit={(e) => handleSubmit(e)} ref={formRef}>
                        <div className="cw-form-container">
                            <div className="form-inner mx-auto">
                                <h5>Car Wash Details</h5>

                                <CWInput
                                    id="washname"
                                    name="washname"
                                    value={formData.washname}
                                    type="text"
                                    label="Car Wash Name"
                                    errorMessage={errors.washname}
                                    maxLength={WASH_NAME_MAX_LENGTH}
                                    isMandatory
                                    onChange={handleInputChange}
                                    autoFocus
                                />
                                <Row>
                                    <Col md={6}>
                                        <CWInput
                                            id="address"
                                            name="address"
                                            type="text"
                                            maxLength={WASH_ADDRESS_MAX_LENGTH}
                                            value={formData.address}
                                            label="Address 1"
                                            errorMessage={errors.address}
                                            isMandatory
                                            onChange={handleInputChange}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CWInput
                                            id="address"
                                            name="address_2"
                                            value={formData.address_2}
                                            maxLength={WASH_ADDRESS_MAX_LENGTH}
                                            type="text"
                                            label="Address 2"
                                            labelSuffix="(Optional)"
                                            errorMessage={errors.address_2}
                                            onChange={handleInputChange}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <CWInput
                                            id="city"
                                            name="city"
                                            type="text"
                                            label="City"
                                            value={formData.city}
                                            maxLength={CITY_MAX_LENGTH}
                                            isMandatory
                                            errorMessage={errors.city}
                                            onChange={handleInputChange}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CWSelect
                                            id="country"
                                            name="country"
                                            options={countryOptions}
                                            values={countryOptions.filter(option => option.value === formData.country)}
                                            label="Country"
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder="Select"
                                            multi={false}
                                            isMandatory
                                            errorMessage={errors.country}
                                            searchable={false}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'country')}
                                            className="no-input"
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <CWSelect
                                            id="state"
                                            name="state"
                                            options={options}
                                            values={options.filter(option => option.value === formData.state)}
                                            label={stateLabel}
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder="Select"
                                            multi={false}
                                            isMandatory
                                            errorMessage={errors.state}
                                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'state')}
                                            className="no-input"
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CWInput
                                            id="zipcode"
                                            name="zipcode"
                                            type="text"
                                            value={formData.zipcode}
                                            label={postalLabel}
                                            isMandatory
                                            maxLength={postalMaxLength}
                                            errorMessage={errors.zipcode}
                                            onChange={handleInputChange}
                                        />
                                    </Col>
                                </Row>
                                <h5 className='mt-3'>Contact Information</h5>
                                <Row>
                                    <Col md={6}>
                                        <CWInput
                                            id="name"
                                            name="name"
                                            type="text"
                                            label="Name"
                                            value={formData.name}
                                            maxLength={NAME_MAX_LENGTH}
                                            isMandatory
                                            onChange={handleInputChange}
                                            errorMessage={errors.name}

                                        />
                                    </Col>
                                    <Col md={6}>
                                        <div className="custom-note position-relative">
                                            <CWInput
                                                id="emailid"
                                                name="emailid"
                                                value={formData.emailid}
                                                type="text"
                                                label="Email Address"
                                                isMandatory
                                                onChange={handleInputChange}
                                                maxLength={EMAIL_MAX_LENGTH}
                                                errorMessage={errors.emailid}

                                            />
                                            <span className="note p-r">This will be used to sign in to the website</span>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <CWInput
                                            id="phonenumber"
                                            name="phonenumber"
                                            type="text"
                                            value={formData.phonenumber}
                                            label="Phone Number"
                                            isMandatory
                                            maxLength={PHONE_MAX_LENGTH}
                                            errorMessage={errors.phonenumber}
                                            onPaste={handlePaste}
                                            onChange={handleInputChange}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CWPasswordInput
                                            id="password"
                                            name="password"
                                            type="password"
                                            value={formData.password}
                                            showpass={<span />}
                                            label="Password"
                                            isMandatory
                                            maxLength={PHONE_MAX_LENGTH}
                                            errorMessage={errors.password}
                                            onPaste={handlePaste}
                                            onChange={handleInputChange}
                                        />
                                    </Col>
                                </Row>

                            </div>
                        </div>
                        <div className="btn-sec d-flex justify-content-end">
                            <Button title="Next" variant="primary" className="primary-btn" type="submit" >Next</Button>
                        </div>
                    </form>

                </div>

            </Container>
            <CwoAddWashSuccessModal
                show={showAddSuccessModal}
                onHide={handleCloseAddSuccessModal}
                carwashid={addedcarwashid}
            />
            {loading && <FullLoader />}
        </div>
    )
}

export default Cwosignup;